import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { morphForWords, roundingNumber } from 'components/shared/processes'
import useMilkingsInfoForGraph from 'hook/query/useMilkingsInfoForGraph'
import { ICowLightInfo } from 'models/ICattlesData'
import moment from 'moment'
import { memo, useMemo, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import DateRangePicker from 'ui/DatePickerInput/DateRangePicker'
import { ExpiredLicense } from 'ui/expired-license/ExpiredLicense'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import YieldQtyChart from '../shared/YieldQtyChart'
import YieldQtyHistory from '../shared/YieldQtyHistory'

interface ILactationOption {
	id: string
	number: number
	startDate: number
	endDate: number
	days: number
}

interface IParsedLactationOption {
	id: string
	number: number
	startDate: number
	endDate: number | null
	endDateByDays: number | null
	days: number | null
}

const LACTATION_LIST_KEYS = [
	'lactations',
	'lactation_history',
	'lactationHistory',
	'calving_history',
	'calvingHistory',
	'calving_dates',
	'calvingDates',
] as const

const parseToTimestamp = (value: unknown): number | null => {
	if (value == null) return null

	if (typeof value === 'number') {
		return value < 1e12 ? value * 1000 : value
	}

	if (typeof value === 'string') {
		const numericValue = Number(value)
		if (!Number.isNaN(numericValue)) {
			return numericValue < 1e12 ? numericValue * 1000 : numericValue
		}
		const parsedByFormat = moment(
			value,
			['DD.MM.YYYY', 'DD.MM.YY', 'YYYY-MM-DD', moment.ISO_8601],
			true,
		)
		if (parsedByFormat.isValid()) {
			return parsedByFormat.valueOf()
		}

		const parsed = moment(value)
		return parsed.isValid() ? parsed.valueOf() : null
	}

	return null
}

const parseNumber = (value: unknown): number | null => {
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value === 'string') {
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? numericValue : null
	}
	return null
}

const getLactationDays = (startDate: number, endDate: number): number => {
	const diff = moment(endDate)
		.startOf('day')
		.diff(moment(startDate).startOf('day'), 'days')
	return diff > 0 ? diff : 0
}

const getPreviousLactationEndDate = (nextLactationStartDate: number): number =>
	moment(nextLactationStartDate).startOf('day').subtract(1, 'day').valueOf()

const buildLactationOption = (
	record: Record<string, unknown>,
	index: number,
): IParsedLactationOption | null => {
	const startDate =
		parseToTimestamp(record.lactation_start_dt) ??
		parseToTimestamp(record.lactation_start) ??
		parseToTimestamp(record.lactation_start_date) ??
		parseToTimestamp(record.start_date) ??
		parseToTimestamp(record.startDate) ??
		parseToTimestamp(record.calving_date) ??
		parseToTimestamp(record.date)
	const parsedEndDate =
		parseToTimestamp(record.lactation_end_dt) ??
		parseToTimestamp(record.lactation_end) ??
		parseToTimestamp(record.lactation_end_date) ??
		parseToTimestamp(record.end_date) ??
		parseToTimestamp(record.endDate) ??
		parseToTimestamp(record.previous_calving_date) ??
		parseToTimestamp(record.prev_calving_date)
	const lactationNumber =
		parseNumber(record.number_lactation) ??
		parseNumber(record.lactation_number) ??
		parseNumber(record.lactation_num) ??
		parseNumber(record.lactation) ??
		parseNumber(record.lactation_count) ??
		parseNumber(record.number)

	if (!startDate) return null

	const lactationDays =
		parseNumber(record.days) ??
		parseNumber(record.lactation_days) ??
		parseNumber(record.lactation_length)
	const endDateByDays = lactationDays
		? moment(startDate)
				.startOf('day')
				.add(Math.max(lactationDays - 1, 0), 'days')
				.valueOf()
		: null

	if (parsedEndDate && parsedEndDate < startDate) return null

	return {
		id: `${lactationNumber ?? index}-${startDate}-${parsedEndDate ?? ''}`,
		number: lactationNumber ?? index + 1,
		startDate,
		endDate: parsedEndDate,
		endDateByDays,
		days: lactationDays,
	}
}

const fillLactationPeriods = (
	options: IParsedLactationOption[],
): ILactationOption[] => {
	const sortedByStartDate = [...options].sort(
		(a, b) => a.startDate - b.startDate,
	)
	const fallbackEndDate = moment().endOf('day').valueOf()

	return sortedByStartDate
		.map((item, index) => {
			const nextLactationStartDate = sortedByStartDate[index + 1]?.startDate
			const endDateByNextLactation = nextLactationStartDate
				? getPreviousLactationEndDate(nextLactationStartDate)
				: null
			const endDate =
				item.endDate ??
				endDateByNextLactation ??
				item.endDateByDays ??
				fallbackEndDate

			if (endDate < item.startDate) return null

			const days = item.days ?? getLactationDays(item.startDate, endDate)

			return {
				id: `${item.number}-${item.startDate}-${endDate}`,
				number: item.number,
				startDate: item.startDate,
				endDate,
				days,
			}
		})
		.filter((item): item is ILactationOption => item !== null)
}

const getLactationOptions = (
	cowInfo: ICowLightInfo | (ICowLightInfo & Record<string, unknown>),
): ILactationOption[] => {
	const info = cowInfo as ICowLightInfo & Record<string, unknown>
	const listKey = LACTATION_LIST_KEYS.find(key => Array.isArray(info[key]))
	if (!listKey) return []

	const list = info[listKey] as unknown[]
	if (list.length === 0) return []

	if (typeof list[0] === 'number' || typeof list[0] === 'string') {
		const dates = list
			.map(item => parseToTimestamp(item))
			.filter((item): item is number => item !== null)
			.sort((a, b) => b - a)

		return dates
			.slice(1)
			.map((startDate, index) => {
				const endDate = getPreviousLactationEndDate(dates[index])
				return {
					id: `lactation-${index + 1}-${startDate}-${endDate}`,
					number: dates.length - (index + 1),
					startDate,
					endDate,
					days: getLactationDays(startDate, endDate),
				}
			})
			.sort((a, b) => b.number - a.number)
	}

	const records = (list as Record<string, unknown>[])
		.map((item, index) => buildLactationOption(item, index))
		.filter((item): item is IParsedLactationOption => item !== null)

	return fillLactationPeriods(records)
		.filter(
			(item, index, arr) =>
				arr.findIndex(option => option.id === item.id) === index,
		)
		.sort((a, b) => b.number - a.number)
}

const getFallbackCurrentLactation = (
	cowInfo: ICowLightInfo | (ICowLightInfo & Record<string, unknown>),
): ILactationOption[] => {
	const lactationLength = parseNumber(cowInfo.lactation_length)
	const lactationCount = parseNumber(cowInfo.lactation_count)

	if (!lactationLength || lactationLength <= 0 || !lactationCount) {
		return []
	}

	const endDate = moment().endOf('day').valueOf()
	const startDate = moment()
		.startOf('day')
		.subtract(Math.max(lactationLength - 1, 0), 'days')
		.valueOf()

	return [
		{
			id: `fallback-current-lactation-${lactationCount}-${startDate}-${endDate}`,
			number: lactationCount,
			startDate,
			endDate,
			days: lactationLength,
		},
	]
}

interface YieldQtyCardProps {
	cowId: number
	cowInfo: ICowLightInfo
}

const YieldQtyCard: React.FC<YieldQtyCardProps> = memo(props => {
	const { cowId, cowInfo } = props
	const { userData } = useAppSelector(state => state.userReducer)
	const {
		milkingInfo,
		categories,
		timestamp,
		isLoading,
		onChangeCallback,
		daysDifference,
		dateRange,
	} = useMilkingsInfoForGraph(cowId)
	const lactationOptions = useMemo(() => {
		const optionsFromHistory = getLactationOptions(cowInfo)
		if (optionsFromHistory.length > 0) {
			return optionsFromHistory
		}

		return getFallbackCurrentLactation(cowInfo)
	}, [cowInfo])
	const [selectedLactationId, setSelectedLactationId] = useState<string | null>(
		null,
	)
	const selectedLactation = lactationOptions.find(
		item => item.id === selectedLactationId,
	)
	const [lactationAnchorEl, setLactationAnchorEl] =
		useState<null | HTMLElement>(null)
	const isLactationOpen = Boolean(lactationAnchorEl)

	return (
		<CardStandart
			sx={{
				padding: '24px',
				display: 'flex',
				flexDirection: 'column',
				gap: '24px',
				minHeight: '400px',
			}}
		>
			<TypographyHeader>Суточный надой</TypographyHeader>
			<DateRangePicker
				onChangeUpdateDate={onChangeCallback}
				startDate={dateRange.startDate}
				endDate={dateRange.endDate}
				weekFilter={true}
				monthFilter={true}
				yearFilter={true}
				currentRange={'Неделя'}
				onPresetSelect={() => setSelectedLactationId(null)}
				presetResetKey={selectedLactationId}
				middleContent={
					lactationOptions.length > 0 ? (
						<>
							<Button
								variant='outlined'
								disableRipple
								onClick={event => setLactationAnchorEl(event.currentTarget)}
								endIcon={
									<KeyboardArrowDownIcon
										sx={{
											color:
												isLactationOpen || selectedLactation
													? COLORS.main
													: COLORS.secondary,
											transform: isLactationOpen
												? 'rotate(180deg)'
												: 'rotate(0deg)',
										}}
									/>
								}
								sx={{
									height: '40px',
									minWidth: '138px',
									padding: '8px 14px',
									borderRadius: '12px',
									textTransform: 'none',
									fontSize: '16px',
									lineHeight: '20px',
									fontWeight: 400,
									borderColor:
										isLactationOpen || selectedLactation
											? COLORS.main
											: COLORS.inputBorder,
									color:
										isLactationOpen || selectedLactation
											? COLORS.main
											: COLORS.secondary,
									'&:hover': {
										borderColor:
											isLactationOpen || selectedLactation
												? COLORS.main
												: COLORS.hoverInputBorder,
										backgroundColor: 'transparent',
									},
								}}
							>
								{selectedLactation ? (
									<>
										<Box component='span' sx={{ fontWeight: 600 }}>
											Лактация
										</Box>
										<Box
											component='span'
											sx={{ marginLeft: '4px', fontWeight: 600 }}
										>
											{selectedLactation.number}
										</Box>
									</>
								) : (
									<Box component='span' sx={{ fontWeight: 600 }}>
										Лактация
									</Box>
								)}
							</Button>
							<Menu
								anchorEl={lactationAnchorEl}
								open={isLactationOpen}
								onClose={() => setLactationAnchorEl(null)}
								transformOrigin={{ horizontal: 'left', vertical: 'top' }}
								anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
								PaperProps={{
									sx: {
										marginTop: '8px',
										borderRadius: '12px',
										boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
										padding: '4px',
										minWidth: '180px',
									},
								}}
							>
								{lactationOptions.map(item => (
									<MenuItem
										key={item.id}
										onClick={() => {
											const selected = lactationOptions.find(
												option => option.id === item.id,
											)
											if (!selected) return

											setSelectedLactationId(selected.id)
											onChangeCallback([selected.startDate, selected.endDate])
											setLactationAnchorEl(null)
										}}
										sx={{
											minHeight: '34px',
											padding: '6px 10px',
											borderRadius: '8px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: '16px',
											'&:hover': {
												backgroundColor: COLORS.mainSecondaryOpacity,
											},
										}}
									>
										<Box
											component='span'
											sx={{
												fontSize: '14px',
												lineHeight: '20px',
												fontWeight: 400,
												color: COLORS.black,
											}}
										>
											{`Лактация ${item.number}`}
										</Box>
										<Box
											component='span'
											sx={{
												fontSize: '14px',
												lineHeight: '20px',
												fontWeight: 400,
												color: COLORS.secondary,
												whiteSpace: 'nowrap',
											}}
										>
											{`${item.days} ${morphForWords(item.days, [
												'день',
												'дня',
												'дней',
											])}`}
										</Box>
									</MenuItem>
								))}
							</Menu>
						</>
					) : undefined
				}
			/>
			<Box display={'flex'} flexDirection={'column'} gap={'4px'}>
				<TypographySecondary>
					{daysDifference === 0 ? 'Итого за день' : 'Итого за период'}
				</TypographySecondary>
				{milkingInfo && (
					<TypographyPrimary16Bold>
						{roundingNumber(milkingInfo.total_milk_sum, 2)}
						<span style={{ fontWeight: 400, fontSize: '12px' }}>{' л'}</span>
					</TypographyPrimary16Bold>
				)}
			</Box>
			{userData?.isIdentificationExpired ? (
				<ExpiredLicense />
			) : (
				<>
					{categories && timestamp && (
						<>
							<YieldQtyChart
								isLoading={isLoading}
								categories={categories}
								timestamp={timestamp}
								dateBetweenDates={daysDifference}
							/>
							<YieldQtyHistory milkingInfo={milkingInfo?.graph} />
						</>
					)}
				</>
			)}
		</CardStandart>
	)
})

export default YieldQtyCard

YieldQtyCard.displayName = 'YieldQtyCard'
